import { useMemo } from 'react';

/**
 * ClosedWeeksSparkline — micro-grafico SVG (no dipendenze esterne) per
 * mostrare il trend di item chiusi nelle ultime N settimane ISO.
 *
 * Props:
 *   - weeks: array `[{label, total, mit, azioni, start, end}]` (cronologico)
 *   - width, height: dimensioni in px (default 88 × 22)
 *
 * Render: area chart emerald + linea sopra + dot sull'ultima settimana.
 * Tooltip nativo via <title> sull'SVG (testo aggregato delle 8 settimane).
 */
export default function ClosedWeeksSparkline({ weeks, width = 88, height = 22 }) {
  const { areaPath, linePath, lastDot, tooltipText, hasData } = useMemo(() => {
    if (!Array.isArray(weeks) || weeks.length === 0) {
      return { areaPath: '', linePath: '', lastDot: null, tooltipText: '', hasData: false };
    }
    const values = weeks.map((w) => w.total || 0);
    const maxVal = Math.max(1, ...values); // evita div/0
    const stepX = weeks.length > 1 ? width / (weeks.length - 1) : width;
    const padY = 2;
    const usableH = height - padY * 2;

    const points = values.map((v, i) => {
      const x = i * stepX;
      const y = padY + usableH - (v / maxVal) * usableH;
      return [x, y];
    });

    const linePath = points
      .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
      .join(' ');

    // Area chart: linea + chiusura verso il bottom
    const areaPath = `${linePath} L${(points[points.length - 1][0]).toFixed(1)},${height} L0,${height} Z`;

    const [lx, ly] = points[points.length - 1];
    const lastDot = { x: lx, y: ly, value: values[values.length - 1] };

    // Tooltip aggregato (hover sull'SVG)
    const tooltipText = weeks
      .map((w) => `${w.label.slice(-3)}: ${w.total}`) // W26: 10
      .join(' · ');

    return { areaPath, linePath, lastDot, tooltipText, hasData: maxVal > 0 };
  }, [weeks, width, height]);

  if (!hasData) return null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="ml-1 inline-block align-middle"
      role="img"
      aria-label={`Trend ultime ${weeks.length} settimane: ${tooltipText}`}
      data-testid="closed-weeks-sparkline"
    >
      <title>{tooltipText}</title>
      <path d={areaPath} fill="rgb(16 185 129 / 0.18)" stroke="none" />
      <path d={linePath} fill="none" stroke="rgb(16 185 129)" strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round" />
      {lastDot && (
        <circle cx={lastDot.x} cy={lastDot.y} r={2} fill="rgb(5 150 105)" />
      )}
    </svg>
  );
}
